import React, { useEffect, useRef, useState } from 'react';
import { Music2, VolumeX } from 'lucide-react';

const MUSIC_KEY = 'qline:background-music-v1';
const MUSIC_VOLUME = 0.12;
const MUSIC_PARTS = [
  '/audio/qline-theme-01.b64',
  '/audio/qline-theme-02.b64',
  '/audio/qline-theme-03.b64',
  '/audio/qline-theme-04.b64',
  '/audio/qline-theme-05.b64',
  '/audio/qline-theme-06.b64',
  '/audio/qline-theme-07.b64',
  '/audio/qline-theme-08.b64',
  '/audio/qline-theme-09.b64',
  '/audio/qline-theme-10.b64',
];

function readMusicPreference() {
  try { return globalThis.localStorage?.getItem(MUSIC_KEY) !== 'off'; }
  catch { return true; }
}

function saveMusicPreference(enabled) {
  try { globalThis.localStorage?.setItem(MUSIC_KEY, enabled ? 'on' : 'off'); }
  catch {}
}

async function createThemeUrl() {
  const responses = await Promise.all(MUSIC_PARTS.map((path) => fetch(path, { cache: 'force-cache' })));
  if (responses.some((response) => !response.ok)) throw new Error('QLine theme music is unavailable.');

  const chunks = await Promise.all(responses.map(async (response) => {
    const encoded = (await response.text()).trim();
    const binary = globalThis.atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return bytes;
  }));

  return URL.createObjectURL(new Blob(chunks, { type: 'audio/mpeg' }));
}

export function BackgroundMusic() {
  const [enabled, setEnabled] = useState(readMusicPreference);
  const enabledRef = useRef(enabled);
  const audioRef = useRef(null);
  const objectUrlRef = useRef('');
  const unlockRef = useRef(null);

  function disarmUnlock() {
    if (!unlockRef.current) return;
    document.removeEventListener('pointerdown', unlockRef.current, true);
    document.removeEventListener('keydown', unlockRef.current, true);
    unlockRef.current = null;
  }

  function armUnlock() {
    if (unlockRef.current || !enabledRef.current) return;
    const retry = () => {
      const audio = audioRef.current;
      if (!audio || !enabledRef.current) return;
      audio.play().then(disarmUnlock).catch(() => {});
    };
    unlockRef.current = retry;
    document.addEventListener('pointerdown', retry, true);
    document.addEventListener('keydown', retry, true);
  }

  useEffect(() => {
    let disposed = false;
    createThemeUrl().then((url) => {
      if (disposed) { URL.revokeObjectURL(url); return; }
      objectUrlRef.current = url;
      const audio = new Audio(url);
      audio.loop = true;
      audio.preload = 'auto';
      audio.volume = MUSIC_VOLUME;
      audioRef.current = audio;
      if (enabledRef.current) audio.play().then(disarmUnlock).catch(armUnlock);
    }).catch(() => {});

    return () => {
      disposed = true;
      disarmUnlock();
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      }
      audioRef.current = null;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
    };
  }, []);

  function toggleMusic() {
    const next = !enabledRef.current;
    enabledRef.current = next;
    setEnabled(next);
    saveMusicPreference(next);
    const audio = audioRef.current;
    if (!audio) return;
    if (!next) {
      disarmUnlock();
      audio.pause();
      return;
    }
    audio.play().then(disarmUnlock).catch(armUnlock);
  }

  return <button
    type="button"
    className={`background-music-toggle ${enabled ? 'playing' : 'muted'}`}
    onClick={toggleMusic}
    aria-pressed={enabled}
    aria-label={enabled ? 'Turn background music off' : 'Turn background music on'}
    title={enabled ? 'Music on' : 'Music off'}
  >
    {enabled ? <Music2 size={17} aria-hidden="true" /> : <VolumeX size={17} aria-hidden="true" />}
    <span>{enabled ? 'Music on' : 'Music off'}</span>
  </button>;
}
